import { useDispatch } from 'react-redux';
import Modal from '../../../../Common/Modal';
import styles from '../EditPhone/EditPhone.module.scss';
import { Button } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clientActions } from '../../../store';

const EditPassword = ({ client_id, isModalOpen, setIsModalOpen }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isModalOpen) return;
    setNewPassword('');
    setConfirmPassword('');
    setSubmitting(false);
    setError('');
  }, [isModalOpen]);

  const canSave = useMemo(
    () => Boolean(newPassword.trim() && confirmPassword.trim()),
    [newPassword, confirmPassword],
  );

  const closeModal = () => {
    setNewPassword('');
    setConfirmPassword('');
    setSubmitting(false);
    setError('');
    setIsModalOpen(false);
  };

  const handleSave = () => {
    if (!canSave || submitting) return;

    if (!newPassword) {
      setError(t('clients.clientModal.editPassword.passwordRequired'));
      return;
    }
    if (newPassword.length < 6) {
      setError(t('clients.clientModal.editPassword.passwordTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('clients.clientModal.editPassword.passwordsDoNotMatch'));
      return;
    }

    setError('');
    setSubmitting(true);
    dispatch(
      clientActions.EDIT_CLIENT_PASSWORD.REQUEST(
        {
          idutente: client_id,
          password: newPassword,
        },
        () => {
          dispatch(
            clientActions.GET_CLIENT.REQUEST({ idcliente: client_id }, () => {
              setSubmitting(false);
              setIsModalOpen(false);
            }),
          );
        },
      ),
    );
    window.setTimeout(() => setSubmitting(false), 2500);
  };

  return (
    <Modal
      isModalOpen={isModalOpen}
      setIsModalOpen={closeModal}
      title={t('clients.clientModal.editPassword.title')}
      maxWidth="min(400px, calc(100vw - 32px))"
      width="100%"
      height="auto"
      maxHeight="90dvh"
      showCloseBtn
      centered
      borderRadius="16px"
    >
      <div className={styles.shell}>
        <div className={styles.body}>
          <div className={styles.intro}>
            <span className={styles.intro_icon} aria-hidden="true">
              <KeyRound size={20} strokeWidth={2.2} />
            </span>
            <div className={styles.intro_copy}>
              <p className={styles.intro_title}>
                {t('clients.clientModal.editPassword.introTitle')}
              </p>
              <p className={styles.intro_text}>
                {t('clients.clientModal.editPassword.introText')}
              </p>
            </div>
          </div>

          <section className={styles.section}>
            <label className={styles.label} htmlFor="edit-client-password">
              {t('clients.clientModal.editPassword.newPasswordLabel')}
            </label>
            <div className={styles.input_wrap}>
              <KeyRound size={16} strokeWidth={2.2} className={styles.input_icon} aria-hidden="true" />
              <input
                id="edit-client-password"
                type="password"
                autoComplete="new-password"
                className={styles.input}
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setError('');
                }}
                placeholder={t('clients.clientModal.editPassword.newPasswordPlaceholder')}
                disabled={submitting}
              />
            </div>
          </section>

          <section className={styles.section}>
            <label className={styles.label} htmlFor="edit-client-password-confirm">
              {t('clients.clientModal.editPassword.confirmPasswordLabel')}
            </label>
            <div className={styles.input_wrap}>
              <KeyRound size={16} strokeWidth={2.2} className={styles.input_icon} aria-hidden="true" />
              <input
                id="edit-client-password-confirm"
                type="password"
                autoComplete="new-password"
                className={styles.input}
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setError('');
                }}
                placeholder={t('clients.clientModal.editPassword.confirmPasswordPlaceholder')}
                disabled={submitting}
              />
            </div>
            {error ? (
              <p className={styles.intro_text} style={{ color: '#d14343', margin: 0 }}>
                {error}
              </p>
            ) : null}
          </section>
        </div>

        <div className={styles.footer}>
          <Button
            variant="contained"
            className={styles.confirm_button}
            onClick={handleSave}
            disabled={!canSave || submitting}
          >
            {t('common.actions.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditPassword;
